import api from '@/libs/axiosInstance';
import type { ApiResponse, MentionDownloadAcknowledgmentStatusDto } from '@fluenceur/types';
import { useQuery } from '@tanstack/react-query';

/**
 * Query key factory for the per-(user, brand) download acknowledgment.
 * Exported so the acknowledge mutation can invalidate without re-importing
 * the constant in two places.
 */
export const downloadAcknowledgmentStatusQueryKey = ['download-acknowledgment-status'] as const;

const fetchStatus = async (): Promise<MentionDownloadAcknowledgmentStatusDto> => {
  const response = await api.get<ApiResponse<MentionDownloadAcknowledgmentStatusDto>>(
    '/dashboard/mentions/download/acknowledgment-status'
  );

  if (response.data.success && response.data.data) {
    return response.data.data;
  }

  throw new Error(response.data.message || 'Failed to fetch download acknowledgment status');
};

/**
 * Returns whether the current user has acknowledged the download disclaimer
 * for the active brand. Stale time stays short so a fresh ack recorded
 * in another tab is reflected quickly.
 */
export const useDownloadAcknowledgmentStatusQuery = () => {
  return useQuery<MentionDownloadAcknowledgmentStatusDto, Error>({
    queryKey: downloadAcknowledgmentStatusQueryKey,
    queryFn: fetchStatus,
    staleTime: 5 * 60 * 1000,
  });
};
